import { useEffect, useState } from 'react';
import { supabase } from '../../services/supabaseService';
import type { MaintenanceChecklistItem } from '../../types/maintenance';

interface Props {
  contractId: string;
  month: string;
  startDate: string;
  canManage: boolean;
  onLogTime?: (title: string) => void;
}
interface ChecklistData { items: MaintenanceChecklistItem[]; closed: boolean; editable: boolean; }
const currentMonth = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Madrid', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date()).slice(0, 7);

export default function MaintenanceChecklist({ contractId, month, startDate, canManage, onLogTime }: Props) {
  const [data, setData] = useState<ChecklistData | null>(null);
  const [revision, setRevision] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [userId, setUserId] = useState('');
  const [title, setTitle] = useState('');
  const firstMonth = [currentMonth(), startDate.slice(0, 7)].sort().slice(-1)[0];
  const [startsMonth, setStartsMonth] = useState([firstMonth, month].sort().slice(-1)[0]);
  useEffect(() => {
    let active = true;
    setLoading(true);
    void (async () => {
      try {
        const [result, auth] = await Promise.all([
          supabase.rpc('get_maintenance_checklist', { p_contract: contractId, p_month: `${month}-01` }),
          supabase.auth.getUser(),
        ]);
        if (result.error) throw result.error;
        if (active) { setData(result.data); setUserId(auth.data.user?.id || ''); setError(''); }
      } catch (e) { if (active) { setData(null); setError((e as Error).message || 'No se pudo cargar la checklist'); } }
      finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [contractId, month, revision]);

  const run = async (action: () => Promise<void>) => {
    setBusy(true); setError('');
    try { await action(); setRevision(v => v + 1); }
    catch (e) { setError((e as Error).message || 'No se pudo guardar la checklist'); }
    finally { setBusy(false); }
  };
  const completed = data?.items.filter(item => item.completed_at).length || 0;
  return <section className="border rounded-xl p-4 space-y-4 bg-white">
    <div><h3 className="font-semibold text-lg">Checklist mensual obligatoria</h3>
      <p className="text-sm text-gray-600">Tareas fijas de cada mes, además de las incidencias que surjan. Cada mes comienza con sus propios checks pendientes.</p>
    </div>
    {error && <p role="alert" className="bg-red-50 text-red-700 p-3 rounded-lg">{error} <button onClick={() => setRevision(v => v + 1)} className="underline">Actualizar lista</button></p>}
    {loading ? <p role="status">Cargando checklist…</p> : data && <>
      <p className="font-medium" role="status">{completed} de {data.items.length} completadas · {month}{data.closed ? ' · Periodo cerrado' : ''}</p>
      {data.items.length === 0 && <p className="text-sm text-gray-600">No hay tareas recurrentes configuradas para este mes.{canManage ? ' Añádelas debajo al preparar el mantenimiento.' : ' Administración configura la lista.'}</p>}
      <ul className="divide-y">
        {data.items.map(item => <li key={item.id} className="py-3 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1"><label className="flex gap-3 items-start">
            <input type="checkbox" className="mt-1" checked={!!item.completed_at}
              disabled={busy || !data.editable || (!!item.completed_at && !canManage && item.completed_by !== userId)}
              onChange={e => { const done = e.target.checked; void run(async () => {
                const result = await supabase.rpc('complete_maintenance_checklist', { p_contract: contractId, p_task: item.id, p_month: `${month}-01`, p_done: done, p_expected: item.completed_at });
                if (result.error) throw result.error;
              }); }} />
            <span className={item.completed_at ? 'text-gray-600' : 'font-medium'}>{item.title}</span>
          </label>
          {item.completed_at && <p className="ml-6 mt-1 text-sm text-green-700">Completada por {item.completed_by_name} · {new Date(item.completed_at).toLocaleString('es-ES', { timeZone: 'Europe/Madrid' })}</p>}
          {item.ends_month && <p className="ml-6 mt-1 text-xs text-gray-500">No se repetirá desde {item.ends_month.slice(0, 7)}. El historial se conserva.</p>}
          </div>
          <div className="flex gap-3 text-sm">
            {onLogTime && data.editable && <button type="button" onClick={() => onLogTime(item.title)} className="text-blue-700">Registrar tiempo</button>}
            {canManage && !item.ends_month && !data.closed && <button type="button" disabled={busy} className="text-gray-600 underline" onClick={() => {
              if (!confirm('¿Dejar de repetir esta tarea desde el próximo mes? El mes actual y el historial se conservan.')) return;
              void run(async () => { const result = await supabase.rpc('configure_maintenance_checklist', { p_contract: contractId, p_retire: item.id }); if (result.error) throw result.error; });
            }}>Retirar recurrencia</button>}
          </div>
        </li>)}
      </ul>
      <p className="text-xs text-gray-500">Marcar un check no descuenta horas. Registra el tiempo real en las intervenciones del proyecto; se descontará una vez validado.</p>
    </>}
    {canManage && <form className="border-t pt-4 space-y-3" onSubmit={e => { e.preventDefault(); void run(async () => {
      const result = await supabase.rpc('configure_maintenance_checklist', { p_contract: contractId, p_title: title.trim(), p_start: `${startsMonth}-01` });
      if (result.error) throw result.error;
      setTitle('');
    }); }}>
      <h4 className="font-medium">Configurar tareas recurrentes</h4>
      <div className="flex flex-wrap gap-2"><span className="text-sm text-gray-600">Ejemplos:</span>{['Revisar APIs e integraciones', 'Revisar actualizaciones disponibles'].map(example => <button key={example} type="button" className="text-sm text-blue-700 underline" onClick={() => setTitle(example)}>{example}</button>)}</div>
      <div className="grid sm:grid-cols-[1fr_auto] gap-3">
        <label className="text-sm">Tarea obligatoria<input required maxLength={300} value={title} onChange={e => setTitle(e.target.value)} className="block border rounded-lg p-2 w-full" placeholder="Qué se debe revisar todos los meses" /></label>
        <label className="text-sm">Repetir desde<input required type="month" min={firstMonth} value={startsMonth} onChange={e => setStartsMonth(e.target.value)} className="block border rounded-lg p-2 w-full" /></label>
      </div>
      <button disabled={busy || loading || !title.trim()} className="bg-blue-600 text-white px-4 py-2 rounded-lg disabled:opacity-50">Añadir tarea mensual</button>
    </form>}
  </section>;
}
